// Storefront route table. front/src/main.js binds these records to Vue components.
// Admin route checks and the static-page API import this same list, so a route
// added here blocks a CMS page without a second hand-maintained copy.
//
// `component` is a key into the component map in main.js. `catchAll` is stripped
// before createRouter; conflict checks skip it so new pages can use every other path.

function redirectLegacyPhp(to) {
	const phpRouteMap = {
		'giftcertificates.php': '/gift-certificates',
		'orderstatus.php': '/account/orders',
	}
	const phpFile = to.path.split('/').pop()
	const targetPath = phpRouteMap[phpFile] || '/404'
	return { path: targetPath, query: to.query }
}

// Bookmarks from the temporary /account2 redesign. Keeps the sub-path, query, and hash.
export function redirectAccount2(to) {
	const raw = to?.params?.pathMatch
	const rest = (Array.isArray(raw) ? raw.join('/') : raw || '')
		.split('/')
		.filter(Boolean)
		.join('/')
	return {
		path: rest ? `/account/${rest}` : '/account',
		query: to?.query || {},
		hash: to?.hash || '',
	}
}

export const storefrontRoutes = [
	{
		// Runtime path is `/:pathMatch(.*).php`. A backslash before the dot in a JS string is not kept.
		path: '/:pathMatch(.*).php',
		redirect: redirectLegacyPhp,
	},
	{ path: '/', component: 'Home', meta: { title: 'In His Hands Birth Supply' } },
	{ path: '/about-us', component: 'AboutUs', meta: { title: 'About Us | In His Hands Birth Supply' } },
	{ path: '/about-us-new', redirect: '/about-us' },
	{ path: '/sign-up-error', component: 'SignUpError' },
	{ path: '/login', component: 'Login', meta: { breadcrumb: 'Login', title: 'Login | In His Hands Birth Supply' } },
	{ path: '/register', component: 'Login', meta: { breadcrumb: 'Register', title: 'Register | In His Hands Birth Supply' } },
	{
		path: '/welcome',
		component: 'Welcome',
		props: (route) => ({ email: route.query.email }),
		meta: { title: 'Welcome | In His Hands Birth Supply' },
	},
	{
		path: '/account',
		component: 'Account',
		children: [
			{ path: '', component: 'accountOrders', meta: { breadcrumb: 'Your Orders' } },
			{ path: 'addresses', component: 'accountAddresses', meta: { breadcrumb: 'Address Book' } },
			// addresses.vue stays mounted and renders the add/edit drawer over the list for these routes
			{ path: 'addresses/create', component: 'accountAddresses', meta: { breadcrumb: 'Address Book' } },
			{ path: 'addresses/:id', component: 'accountAddresses', meta: { breadcrumb: 'Address Book' } },
			{ path: 'orders', component: 'accountOrders', meta: { breadcrumb: 'Your Orders' } },
			{ path: 'orders/:id', component: 'accountOrderDetails', meta: { breadcrumb: 'Order Details' } },
			{ path: 'recently-viewed', component: 'accountRecentlyViewed', meta: { breadcrumb: 'Recently Viewed Items' } },
			{ path: 'recommended', component: 'accountRecommended', meta: { breadcrumb: 'Recommended for you' } },
			{ path: 'settings', component: 'accountSettings', meta: { breadcrumb: 'Account Details' } },
			{ path: 'birth-kits', component: 'accountBirthKits', meta: { breadcrumb: 'Your Birth Kits' } },
			{ path: 'claims', component: 'accountClaims', meta: { breadcrumb: 'Kit Claims', adminOnly: true } },
			{ path: 'approvals', component: 'accountApprovals', meta: { breadcrumb: 'Pending Approvals', adminOnly: true } },
			{ path: 'lists', component: 'accountWishlists', meta: { breadcrumb: 'Your Wishlists' } },
			{ path: 'lists/create', component: 'accountWishlistForm', meta: { breadcrumb: 'Create' } },
			{ path: 'lists/:id', component: 'accountWishlistForm', meta: { breadcrumb: 'Edit Wishlist' } },
		],
		meta: { title: 'Your Account | In His Hands Birth Supply', protected: true, fallback: '/login', hideBreadcrumb: true },
	},
	{ path: '/account2', redirect: redirectAccount2 },
	{ path: '/account2/:pathMatch(.*)*', redirect: redirectAccount2 },
	{
		path: '/shared-list',
		component: 'sharedWishlist',
		meta: { title: 'Shared Wishlist | In His Hands Birth Supply', breadcrumb: 'Shared Wishlist' },
	},
	{
		path: '/invoice/:id',
		component: 'invoiceTemplate',
		name: 'invoiceTemplate',
		meta: {
			title: 'Invoice | In His Hands Birth Supply',
			protected: true,
			fallback: '/login',
		},
	},
	{ path: '/products/:id', component: 'Product', meta: { usePathKey: true } },
	{ path: '/item/:id', component: 'ItemID', meta: { usePathKey: true } },
	{ path: '/cart', component: 'Cart', meta: { title: 'Your Cart | In His Hands Birth Supply' } },
	{ path: '/checkout', component: 'Checkout', meta: { title: 'Checkout | In His Hands Birth Supply', usePathKey: true } },
	{ path: '/checkout/order-confirmation/:bcId', component: 'OrderConfirmation', meta: { title: 'Order Confirmation | In His Hands Birth Supply' } },
	{ path: '/terms-conditions', component: 'TermsConditions', meta: { title: 'Terms & Conditions | In His Hands Birth Supply' } },
	{ path: '/help-center', component: 'HelpCenter', meta: { title: 'Help Center | In His Hands Birth Supply' } },
	{ path: '/categories', component: 'Category', meta: { usePathKey: true } },
	{ path: '/categories/:id', component: 'Category', meta: { usePathKey: true } },
	{ path: '/brands/:id', component: 'Brand', meta: { usePathKey: true } },
	{ path: '/birthKits/:id', component: 'BirthKit' },
	{ path: '/birthKits/:id/edit', component: 'BirthKitBuilder' },
	{ path: '/birth-kit-order-form', component: 'BirthKitOrderForm' },
	{ path: '/custom-birth-kit-updates', component: 'CustomBirthKitUpdates', meta: { title: 'Custom Birth Kit Updates' } },
	{ path: '/blogs', component: 'CustomBirthKitUpdates', meta: { title: 'Custom Birth Kit Updates' } },
	{ path: '/blogs/:slug', component: 'CustomBirthKitBlogPost', meta: { title: 'Custom Birth Kit Updates' } },
	{ path: '/returns', component: 'ReturnsPortal', meta: { title: 'Returns | In His Hands Birth Supply' } },
	{ path: '/returns-new', redirect: '/returns' },
	{ path: '/final-sale-items', component: 'FinalSaleItems', meta: { title: 'Final Sale Items | In His Hands Birth Supply' } },
	{ path: '/brands', component: 'Brands', meta: { title: 'Brands | In His Hands Birth Supply' } },
	{ path: '/midwife-birth-kits', component: 'Midwife', meta: { title: 'Midwife Birth Kits' } },
	{ path: '/custom-birth-kit-service', component: 'CustomBirthKitService', meta: { title: 'Custom Birth Kit Service' } },
	{ path: '/new-birth-kit-setup', redirect: '/custom-birth-kit-service' },
	{ path: '/setup', redirect: '/custom-birth-kit-service' },
	{ path: '/shipping-info', component: 'Shipping', meta: { title: 'Shipping Info | In His Hands Birth Supply' } },
	{ path: '/lost', component: 'Lost', meta: { title: 'Lost | In His Hands Birth Supply' } },
	{ path: '/faqs', component: 'FAQS', meta: { title: 'FAQs | In His Hands Birth Supply' } },
	{ path: '/terms-and-conditions', component: 'TermsAndConditions', meta: { title: 'Terms & Conditions | In His Hands Birth Supply' } },
	{ path: '/discount-code-terms', component: 'DiscountTerms', meta: { title: 'Discount Code Terms | In His Hands Birth Supply' } },
	{ path: '/sitemap', component: 'SiteMap', meta: { title: 'Sitemap | In His Hands Birth Supply' } },
	{ path: '/sitemap-brands', component: 'SitemapBrands', meta: { title: 'Sitemap Brands | In His Hands Birth Supply' } },
	{ path: '/sitemap-categories', component: 'SitemapCategories', meta: { title: 'Sitemap Categories | In His Hands Birth Supply' } },
	{ path: '/privacy-policy', component: 'PrivacyPolicy', meta: { title: 'Privacy Policy | In His Hands Birth Supply' } },
	{ path: '/sms', component: 'SMS', meta: { title: 'SMS | In His Hands Birth Supply' } },
	{ path: '/birth-pool-setup', component: 'BirthPoolSetup', meta: { title: 'Birth Pool Setup | In His Hands Birth Supply' } },
	{
		path: '/build-your-own-water-birth-package',
		component: 'WaterBirthPackage',
		meta: {
			title: 'Build Your Own Water Birth Package | In His Hands Birth Supply',
			hideBreadcrumb: true,
		},
	},
	{ path: '/newman-doppler-trade-in', component: 'NewmanDopplerTradeInOffer', meta: { title: 'Newman Doppler Trade In | In His Hands Birth Supply' } },
	{ path: '/gift-certificates', component: 'GiftCertificates', meta: { title: 'Gift Certificates | In His Hands Birth Supply' } },
	{ path: '/search', component: 'Search', meta: { title: 'Search | In His Hands Birth Supply' } },
	{
		path: '/dev',
		component: 'DevTools',
		meta: {
			title: 'Dev Tools | In His Hands Birth Supply',
			protected: true,
			adminOnly: true,
			fallback: '/login',
		},
	},
	// Unknown paths load a published CMS page when one exists, otherwise the 404 page.
	{ path: '/:pathMatch(.*)*', component: 'CmsPage', catchAll: true, meta: { title: '404 Not Found', usePathKey: true } },
]

export function storefrontComponentKeys(routes = storefrontRoutes) {
	const keys = new Set()
	for (const route of routes) {
		if (typeof route.component === 'string') keys.add(route.component)
		if (route.children) {
			for (const key of storefrontComponentKeys(route.children)) keys.add(key)
		}
	}
	return [...keys]
}
